import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import initialStyles from '../settings/initialStyles';
import { linkIcons, linkText } from '../settings/linkData';
import ThemeConfigPanel from '../settings/ThemeConfigPanel';
import Notification from '../components/notification/Notification';

import 'react-circular-progressbar/dist/styles.css';
import '../styles/TemplateFive.css';

const themePresets = {
    default: initialStyles,
    dark: {
        backgroundColor: '#333',
        secondaryBackgroundColor: '#555',
        headerBackgroundColor: '#222',
        headerTextColor: '#fff',
        subheaderTextColor: '#555',
        textColor: '#eee',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    },
    light: {
        backgroundColor: '#f9f9f9',
        secondaryBackgroundColor: '#e0e0e0',
        headerBackgroundColor: '#ddd',
        headerTextColor: '#333',
        subheaderTextColor: '#555',
        textColor: '#111',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    }
};

const ViewTemplateFive = ({ template, isDownloading }) => {
    const [styles, setStyles] = useState(initialStyles);
    const [notification, setNotification] = useState(null);

    const handleStyleChange = (e) => {
        const { name, value } = e.target;
        setStyles((prevStyles) => ({
            ...prevStyles,
            [name]: value,
        }));
    };

    const applyPreset = (preset) => {
        setStyles(themePresets[preset]);
    };

    const saveCustomTheme = () => {
        localStorage.setItem('customTheme', JSON.stringify(styles));
        setNotification({ message: 'Custom theme saved!', isError: false });
    };

    const loadCustomTheme = () => {
        const customTheme = localStorage.getItem('customTheme');
        if (customTheme) {
            setStyles(JSON.parse(customTheme));
            setNotification({ message: 'Custom theme loaded!', isError: false });
        } else {
            setNotification({ message: 'No custom theme found!', isError: true });
        }
    };

    const templateData = template || {};

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://venusresume.vercel.app/';
    const imageUrl = templateData?.image?.startsWith('/')
        ? `${baseUrl}${templateData.image}`
        : templateData.image;

    return (
        <div className='holder'>

            {notification && (
                <Notification
                    message={notification.message}
                    isError={notification.isError}
                    onClose={() => setNotification(null)}
                />
            )}

            <div className="template-body template-five" style={{ backgroundColor: styles.backgroundColor }}>
                <div className="template-column-left" style={{ backgroundColor: styles.secondaryBackgroundColor, paddingLeft: "12px" }}>


                    <div className='view-template-five-header'>
                        {template?.personName && <h1 className="template-five-name" style={{ fontSize: `${styles.headerFontSize}em`, color: styles.headerTextColor }}>{template?.personName}</h1>}
                        {imageUrl && <img src={imageUrl} alt={template?.personName} className="template-five-image" />}
                    </div>


                    {templateData.aboutMe && (
                        <div className="profile-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Profile</h3>
                            <p style={{ fontSize: `${styles.bodyFontSize}em`, color: styles.textColor }}>{templateData.aboutMe}</p>
                        </div>
                    )}
                    {templateData.workExperience && templateData.workExperience.length > 0 && (
                        <div className="experience-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Experiences</h3>
                            {templateData.workExperience.map((experience, index) => (
                                <div key={index} className="work-experience-item">
                                    <p className="role" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.role}</p>
                                    <p className="company-location" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.company} / {experience.location}</p>
                                    <p style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                        {experience.location && (
                                            <p className="location">
                                                (
                                                {experience.startDate && new Date(experience.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                                {experience.endDate !== "Present" ? new Date(experience.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                                )
                                            </p>
                                        )}
                                    </p>
                                    <p className="description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.description}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    {templateData.education && Array.isArray(templateData.education) && templateData.education.length > 0 && (
                        <div className="education-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Education</h3>
                            {templateData.education.map((edu, index) => (
                                <div key={index} className="education-item">
                                    <p className="degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.degree}</p>
                                    <p className="institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.institution}</p>
                                    <p style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "200" }}>
                                        (
                                        {edu.startDate && new Date(edu.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                        {edu.endDate !== "Present" ? new Date(edu.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                        )
                                    </p>
                                    <p className="institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>{edu.description}</p>

                                </div>
                            ))}
                        </div>
                    )}

                    {templateData?.certificates && (
                        <div className="education-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Certificates</h3>
                            {templateData?.certificates.map((certificate, index) => (
                                <div key={index} className="education-item">
                                    <p className="degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>Title: {certificate.title}</p>
                                    <p className="institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>Institution: {certificate.institution}</p>
                                    <p className="institution" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                        Date Received: {new Date(certificate.DateReceived).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="template-column-right">
                    {templateData.contact && (
                        <div className="contact-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Contact</h3>
                            {templateData.contact.phone && <p><FontAwesomeIcon icon={faPhone} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.phone}</p>}
                            {templateData.contact.email && <p><FontAwesomeIcon icon={faEnvelope} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.email}</p>}
                            {templateData.contact.address && <p><FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.address}</p>}
                        </div>
                    )}
                    {templateData.skills && (
                        <div className="skills-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Skills</h3>
                            <ul>
                                {templateData.skills.map((skill, index) => (
                                    <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {templateData.softwareSkills && templateData.softwareSkills.length > 0 && (
                        <div className="languages-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Software Skills</h3>
                            <ul>
                                {templateData.softwareSkills.map((skill, index) => (
                                    <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill}</li>
                                ))}
                            </ul>

                        </div>
                    )}

                    {templateData.languages && (
                        <div className="languages-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Languages</h3>
                            <ul>
                                {templateData.languages.map((language, index) => (
                                    <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{language}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {templateData.hobbies && (
                        <div className="hobbies-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Interests</h3>
                            <ul>
                                {templateData.hobbies.map((hobby, index) => (
                                    <li key={index} style={{ fontSize: `${styles.bodyFontSize}em` }}>{hobby}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {templateData.links && (
                        <div className="links-section">
                            <h3 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Links</h3>
                            {Object.entries(templateData.links).map(([key, value]) => (
                                <p key={key}>
                                    <FontAwesomeIcon icon={linkIcons[key] || faLink} />
                                    <a href={value} target="_blank" rel="noopener noreferrer">
                                        <span style={{ fontSize: `${styles.bodyFontSize}em` }}>{linkText[key]}</span>
                                    </a>
                                </p>
                            ))}
                        </div>
                    )}

                </div>
            </div>
            {!isDownloading && (
                <ThemeConfigPanel
                    styles={styles}
                    handleStyleChange={handleStyleChange}
                    applyPreset={applyPreset}
                    saveCustomTheme={saveCustomTheme}
                    loadCustomTheme={loadCustomTheme}
                />
            )}
        </div>
    );
};

export default ViewTemplateFive;
